/* eslint-disable react/jsx-props-no-spreading */
import React, { memo, useEffect } from "react";
import { useDispatch } from "react-redux";
import useInboxScreenController from "../hooks/inbox/controller/useInboxScreenController";
import InboxChatWindowMC from "../screens/retailers/slug/inbox/InboxChatWindowMC";
import InboxRightSidebarMC from "../screens/retailers/slug/inbox/InboxRightSidebarMC";
import RxJOrderPdfModal from "../../components/shared/MessageForm/RxJOrderPdfModal";
import RxJOrderPaymentLink from "../../components/shared/MessageForm/RxJOrderPaymentLink";
import { RxJProductSelectionModal } from "../modules/Products";
import RxJFastAnswersModal from "../modules/FastAnswers/RxJFastAnswersModal";
import IconMC from "../components/IconMC";
import TitleMC from "../components/TitleMC";
import useInboxChatListActions from "../hooks/inbox/actions/useInboxChatListActions";
import { fetchConversationById, FETCH_CONVERSATIONS_SUCCESS } from "../../actions/inbox/conversationActions";
import {
  INBOX_MS_API_ALLOWED_PLATFORMS,
  isInboxMsChatPlatform,
  isWhatsAppMsPlatform
} from "../../services/inbox/inboxMsChatPlatform";
import { resolveCrmInboxChat, chatIdFromRecord } from "../../services/inbox/resolveCrmInboxChat";
import ConversationModelMC from "../models/inbox/ConversationModelMC";

const shouldResolveFromMs = (platform) => (
  isInboxMsChatPlatform(platform)
  || INBOX_MS_API_ALLOWED_PLATFORMS.has(platform)
  || isWhatsAppMsPlatform(platform)
);

const buildStubConversation = ({ convId, platform, customer }) => ({
  id: convId,
  attributes: {
    id: convId,
    platform,
    customer_id: String(customer.id),
    first_name: customer.full_names || customer.first_name,
    last_name: customer.last_name,
    phone: customer.phone,
    email: customer.email,
    whatsapp_name: customer.whatsapp_name,
    web_id: customer.web_id,
    recent_message_date: new Date().toISOString()
  }
});

const InboxScreenSidebar = memo(({
  customer,
  platform,
  closeModal,
  conversationId
}) => {
  const dispatch = useDispatch();
  const { chatWindowProps } = useInboxScreenController({ sidebar: true });
  const { handleSelectConversation } = useInboxChatListActions();

  useEffect(() => {
    if (!customer || !platform) return undefined;

    let cancelled = false;

    const openConversation = ({ chatId, chatPlatform, rawChat = null }) => {
      if (cancelled || !chatId || !chatPlatform) return;

      if (rawChat) {
        dispatch({
          type: FETCH_CONVERSATIONS_SUCCESS,
          payload: { data: [rawChat] }
        });
      } else if (INBOX_MS_API_ALLOWED_PLATFORMS.has(chatPlatform)) {
        dispatch({
          type: FETCH_CONVERSATIONS_SUCCESS,
          payload: {
            data: [buildStubConversation({ convId: chatId, platform: chatPlatform, customer })]
          }
        });
      } else {
        dispatch(fetchConversationById({ conversationId: chatId, platform: chatPlatform }));
      }

      handleSelectConversation({
        id: chatId,
        platform: chatPlatform,
        customerId: customer.id
      });
    };

    const openFromHint = () => {
      openConversation({
        chatId: conversationId || customer.id,
        chatPlatform: platform
      });
    };

    if (!shouldResolveFromMs(platform)) {
      openFromHint();
      return undefined;
    }

    ConversationModelMC.getMicroserviceCustomerChats({
      customerId: customer.id
    })
      .then((response) => {
        if (cancelled) return;

        const resolved = resolveCrmInboxChat(response?.data || [], {
          conversationId,
          platform
        });

        if (!resolved) {
          openFromHint();
          return;
        }

        openConversation({
          chatId: chatIdFromRecord(resolved),
          chatPlatform: resolved.attributes?.platform,
          rawChat: resolved
        });
      })
      .catch(() => {
        if (!cancelled) openFromHint();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="tw-wrapper tw-flex tw-flex-col tw-h-screen">
      <div className="tw-flex tw-justify-between tw-items-center tw-border-b tw-px-4 tw-py-4">
        <div className="tw-flex tw-justify-center">
          <div className="tw-flex">
            <TitleMC text="Chat" textClassName="tw-text-base tw-font-semibold tw-text-gray-600" />
          </div>
        </div>
        <div className="tw-flex tw-items-center">
          <IconMC
            name="close-outline"
            onClick={() => closeModal('InboxScreenSidebar')}
          />
        </div>
      </div>
      <div className="tw-flex tw-flex-row tw-flex-1 tw-min-h-0">
        <div className="tw-hidden md:tw-flex tw-flex-1 tw-h-full tw-flex-col tw-min-w-0">
          <InboxChatWindowMC {...chatWindowProps} isSidebar />
        </div>

        <div className="tw-hidden xl:tw-flex tw-h-full tw-bg-white tw-flex-shrink-0 tw-relative">
          <InboxRightSidebarMC />
          <RxJOrderPdfModal className="tw-absolute tw-top-0 tw-right-0 tw-flex tw-flex-col tw-h-full tw-bg-white tw-border-l tw-border-gray-200 tw-w-72 tw-p-4" />
          <RxJOrderPaymentLink className="tw-absolute tw-top-0 tw-right-0 tw-flex tw-flex-col tw-h-full tw-bg-white tw-border-l tw-border-gray-200 tw-w-72 tw-p-4" />
          <RxJProductSelectionModal className="tw-absolute tw-top-0 tw-right-0 tw-flex tw-flex-col tw-h-full tw-bg-white tw-border-l tw-border-gray-200 tw-w-72 tw-p-2" />
          <RxJFastAnswersModal
            className="tw-absolute tw-top-0 tw-right-0 tw-flex tw-flex-col tw-h-full tw-bg-white tw-border-l tw-border-gray-200 tw-w-72"
            customer={customer}
            platform={platform}
          />
        </div>
      </div>
    </div>
  );
});

InboxScreenSidebar.displayName = "InboxScreenSidebar";

export default InboxScreenSidebar;
